import { useState, useEffect, useCallback, useRef } from 'react';
import { get } from '../services/api';

interface UseStatisticsOptions {
  period?: '7d' | '30d' | '90d' | 'custom';
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}

interface UserStatistics {
  userId: string;
  userName: string;
  userEmail: string;
  totalAttendanceDays: number;
  totalTasksCompleted: number;
  totalTasksCreated: number;
  totalTasksAssigned?: number;
  overdueTasks: number;
  completionRate: number;
}

interface DailyBreakdown {
  date: string;
  attendance: number;
  tasksCompleted: number;
  tasksCreated: number;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface GeneralStatistics {
  period: {
    startDate: string;
    endDate: string;
  };
  summary: {
    totalUsers: number;
    totalAttendanceDays: number;
    totalTasksCompleted: number;
    totalTasksCreated: number;
    totalOverdueTasks: number;
    averageCompletionRate: number;
  };
  byUser: UserStatistics[];
  dailyBreakdown: DailyBreakdown[];
  trend: {
    attendanceChange: number;
    completionChange: number;
    creationChange: number;
    overdueChange: number;
  };
  pagination?: PaginationInfo;
}

interface UseStatisticsReturn {
  data: GeneralStatistics | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Custom hook for fetching admin statistics
 * REV-9: Added request cancellation with AbortController
 * REV-10: Improved API error response type handling
 */
export function useStatistics(options: UseStatisticsOptions): UseStatisticsReturn {
  const [data, setData] = useState<GeneralStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // REV-9: Use ref to track the current AbortController
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);

      const params: Record<string, string> = {};
      if (options.period && options.period !== 'custom') {
        params.period = options.period;
      }
      if (options.startDate) {
        params.startDate = options.startDate;
      }
      if (options.endDate) {
        params.endDate = options.endDate;
      }
      if (options.page) {
        params.page = String(options.page);
      }
      if (options.limit) {
        params.limit = String(options.limit);
      }

      const response = await get<GeneralStatistics>('/statistics', params);
      
      // REV-9: Check if request was aborted before processing response
      if (signal?.aborted) {
        return;
      }
      
      if (response.success && response.data) {
        setData(response.data);
        setError(null);
      } else {
        // REV-10: Improved error response type handling
        const errorMessage = extractErrorMessage(response.error);
        setError(errorMessage);
      }
    } catch (err) {
      // REV-9: Don't update state if request was aborted
      if (signal?.aborted) {
        return;
      }
      
      // REV-10: Improved error handling
      const message = extractErrorMessage(err);
      setError(message);
    } finally {
      // REV-9: Only update loading state if not aborted
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [options.period, options.startDate, options.endDate, options.page, options.limit]);

  useEffect(() => {
    // REV-9: Cancel any pending request before starting a new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create a new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    fetchData(abortController.signal);

    // REV-9: Cleanup function to abort on unmount or dependency change
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  /**
   * REV-STAT-7: Abort any in-flight request before starting a new fetch.
   * This prevents race conditions where an older request arrives after a newer one.
   */
  const refetch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    fetchData(abortController.signal);
  }, [fetchData]);

  return { data, loading, error, refetch };
}

/**
 * REV-10: Helper function to extract error message from various error formats
 */
function extractErrorMessage(error: unknown): string {
  if (!error) {
    return 'An unknown error occurred';
  }
  
  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }
  
  // Handle Error objects
  if (error instanceof Error) {
    return error.message;
  }
  
  // Handle API error response objects
  if (typeof error === 'object') {
    const errorObj = error as Record<string, unknown>;
    
    // Check for message property
    if (typeof errorObj.message === 'string') {
      return errorObj.message;
    }
    
    // Check for error property
    if (typeof errorObj.error === 'string') {
      return errorObj.error;
    }
    
    // Check for nested error object with message
    if (errorObj.error && typeof errorObj.error === 'object') {
      const nestedError = errorObj.error as Record<string, unknown>;
      if (typeof nestedError.message === 'string') {
        return nestedError.message;
      }
    }
  }
  
  return 'Failed to load statistics';
}

export type { GeneralStatistics, UserStatistics, DailyBreakdown, PaginationInfo };